import { useTranslation } from 'next-i18next';
import type { IbhDimensions, LifetimeParameters, ShapeDescriptor } from '@hub/bearing-hub/common/types';
import { MetricType } from '@hub/libs/constants';
import { bearingProductionMethods, ShapeDimensionsDefinitionUsp } from '@hub/bearing-hub/common/constants';
import { getDimensionLabel, getProcedureText } from '@hub/bearing-hub/common/utils';
import { getRedirectLinkToResults } from '@hub/bearing-hub/common/link.factory';
import Link from 'next/link';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useCopyLinkToClipboard } from '@hub/hooks/useCopyToClipboard';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProcedure, IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import { getArticleName } from '@hub/bearing-hub/common/articleName.factory';

interface ProductDetailsListProps {
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto;
  selectedShape: ShapeDescriptor;
  lifetimeParameters: LifetimeParameters;
  quantity: number;
  locale: string;
  lifetime: number;
  setActiveLifeTimePopUp: (value: boolean) => void;
}

const materialBrand = {
  igutex: 'igutex',
  iglidur: 'iglidur',
} as const;

export const ProductDetailsUsp = ({
  articleData,
  selectedShape,
  lifetimeParameters,
  quantity,
  locale,
  lifetime,
  setActiveLifeTimePopUp,
}: ProductDetailsListProps) => {
  const { t } = useTranslation();
  const { copyLinkToClipboard } = useCopyLinkToClipboard(
    t(`SHARE_ARTICLE_NUMBER_TOAST_SUCCESS`, { ns: 'bearing-hub/bearingHub' })
  );

  const { shape, dimensions, unitSystem } = articleData;
  const { material, articleNumber } = articleData;
  const imperialArticleNumber = getArticleName(locale, articleData as IbhCatalogArticleResponseDto);
  const resetDimensions = useDimensionsStore(state => state.reset);
  const materialBrandIgutex = articleData.material.name.includes(materialBrand.igutex);
  const productionMethod = materialBrandIgutex ? bearingProductionMethods.WOUND : articleData.productionMethod;

  const productDetailsData = [
    {
      detailName: t(`COMMON.ARTICLE_NUMBER`, { ns: 'bearing-hub/bearingHub' }),
      detailData: articleNumber.includes('TEMP-') ? (
        t(`COMMON.CUSTOM_PART_INFO_TEXT`, { ns: 'bearing-hub/bearingHub' })
      ) : (
        <span className="space-x-2">
          <span>{articleNumber}</span>
          <button
            type="button"
            data-testid="copy-article-number"
            onClick={async () => {
              await copyLinkToClipboard(articleNumber);
            }}
          >
            <Icon size={10} color={IconColor.BLACK} id={IconID.COPY_TO_CLIPBOARD} />
          </button>
        </span>
      ),
      showName: true,
    },
    {
      detailName: t(`FORMER_PART`, { ns: 'bearing-hub/bearingHub' }),
      detailData: imperialArticleNumber !== articleNumber && <span>{imperialArticleNumber}</span>,
      showName: imperialArticleNumber !== articleNumber,
    },
    {
      detailName: t(`COMMON.MATERIAL`, { ns: 'bearing-hub/bearingHub' }),
      detailData: material.name,
      showName: true,
    },
    {
      detailName: t(`COMMON.SHAPE`, { ns: 'bearing-hub/bearingHub' }),
      detailData: t(`SHAPES.${shape}.TITLE`, { ns: 'bearing-hub/bearingHub' }),
      showName: true,
    },
    {
      detailName: t(`COMMON.DIMENSIONS`, { ns: 'bearing-hub/bearingHub' }),
      detailData: (
        <ul className="pl-8 pt-4 list-disc">
          {ShapeDimensionsDefinitionUsp[shape].map(
            (dimension: keyof IbhDimensions) =>
              dimensions[dimension] && (
                <li key={dimension}>
                  {`${getDimensionLabel(dimension, shape)} ${dimensions[dimension]} ${
                    unitSystem === IbhUnitSystem.Imperial ? MetricType.INCH : MetricType.MM
                  }`}
                </li>
              )
          )}
        </ul>
      ),
      showName: true,
    },
    {
      detailName: t(`COMMON.ADJUST_DIMENSIONS`, { ns: 'bearing-hub/bearingHub' }),
      detailData: (
        <Link
          className="text-igus-primary"
          onClick={() => resetDimensions()}
          href={getRedirectLinkToResults(selectedShape, quantity, lifetimeParameters, locale, unitSystem)}
        >
          {t(`COMMON.ADJUST_DIMENSIONS`, { ns: 'bearing-hub/bearingHub' })}
        </Link>
      ),
      showName: false,
    },
    {
      detailName: t(`COMMON.PRODUCTION_METHOD`, { ns: 'bearing-hub/bearingHub' }),
      detailData: getProcedureText(
        productionMethod as IbhProductionMethods,
        t,
        productionMethod === IbhProductionMethods.SEMIFINISHED ? IbhProcedure.CNC : material.procedure
      ),
      showName: true,
    },
    {
      detailName: t('HEADLINES.LIFETIME', { ns: 'bearing-hub/bearingHub' }),
      detailData: lifetime ? (
        <div className="flex items-center ml-2">
          <p className="whitespace-nowrap" data-testid="product-data-lifetime">
            {lifetime === -1
              ? t(`LIFETIME.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })
              : `${new Intl.NumberFormat(locale).format(Math.ceil(lifetime))} h`}
          </p>
          <div
            className="ml-2 cursor-pointer flex"
            data-testid="show-life-time-popup-info"
            onClick={() => {
              setActiveLifeTimePopUp(true);
            }}
          >
            <Icon size={6} id={IconID.INFO_CIRCLE_GRAY} />
          </div>
        </div>
      ) : null,
      showName: true,
      styles: 'flex items-start mt-8 min-h-[30px]',
    },
  ];
  return (
    <ul data-testid="product-data-usp" className="mb-8">
      {productDetailsData.map(detail => {
        if (detail.detailData !== '') {
          return (
            <li
              key={detail.detailName}
              data-testid={`product-data-usp-${detail.detailName.replace(/ /g, '-')}`}
              className={`mb-2 ${detail.styles || ''}`}
            >
              {detail.showName && <span className="font-bold">{detail.detailName}: </span>}
              {detail.detailData}
            </li>
          );
        }
        return null;
      })}
    </ul>
  );
};
